import { useCallback, useMemo } from 'react';
import { useRoles } from '../contexts/RolesContext';

const getStoredRole = () => {
  try {
    const stored = localStorage.getItem('user');
    if (!stored) return 'user';
    const user = JSON.parse(stored);
    return user.role || 'user';
  } catch (error) {
    return 'user';
  }
};

const ADMIN_CONFIGURATION_ACTION_DEFAULTS = {
  admin: {
    manageLogo: true,
    editOrgDetails: true,
    manageBanking: true,
    managePermissions: false,
    resetCounters: false
  },
  appAdmin: {
    manageLogo: true,
    editOrgDetails: true,
    manageBanking: true,
    managePermissions: true,
    resetCounters: true
  }
};

const usePermissions = (pageKey, roleOverride) => {
  const { roles } = useRoles();
  const role = roleOverride || getStoredRole();

  const can = useCallback((actionId) => {
    if (!pageKey) return false;

    if (pageKey === 'admin' && ADMIN_CONFIGURATION_ACTION_DEFAULTS[role]?.[actionId] !== undefined) {
      const fallback = ADMIN_CONFIGURATION_ACTION_DEFAULTS[role][actionId];
      const action = roles?.roles?.[role]?.pages?.[pageKey]?.actions?.[actionId];
      const allowed = action?.intended ?? action?.current;
      return allowed === undefined ? Boolean(fallback) : Boolean(allowed);
    }

    const action = roles?.roles?.[role]?.pages?.[pageKey]?.actions?.[actionId];
    const allowed = action?.intended ?? action?.current;
    return Boolean(allowed);
  }, [roles, role, pageKey]);

  const canView = useCallback(() => {
    if (!pageKey) return false;

    const viewConfig = roles?.roles?.[role]?.pages?.[pageKey]?.view;
    const allowed = viewConfig?.intended ?? viewConfig?.current;

    if (pageKey === 'admin' && ['admin', 'appAdmin'].includes(role)) {
      return allowed === undefined ? true : Boolean(allowed);
    }

    return Boolean(allowed);
  }, [roles, role, pageKey]);

  const getAction = useCallback((actionId) => {
    if (!pageKey) return null;
    return roles?.roles?.[role]?.pages?.[pageKey]?.actions?.[actionId] || null;
  }, [roles, role, pageKey]);

  const pagePermissions = useMemo(() => {
    if (!pageKey) return null;
    return roles?.roles?.[role]?.pages?.[pageKey] || null;
  }, [roles, role, pageKey]);

  return {
    role,
    can,
    canView,
    getAction,
    pagePermissions
  };
};

export default usePermissions;
